/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useState } from "react";

import {
  useGetAnalysisSummaryByDataset,
  useGetSchemaDatasetByAnalysis
} from "@/api/analysis/useAnalysisAPI";
import { useGetUserProfileAPI } from "@/api/userProfile/useUserProfileAPI";
import { DatasetSectionViewType } from "@/components/analysisSummary/filterByGrid/datasetSection";
import { AnalysisConfiguration } from "@/components/analysisSummary/filterByGrid/types";
import {
  getDatasetSummarySortByOptions,
  getDefaultAnalysisConfig,
  getDefaultViewType,
  getFilteredGrids
} from "@/components/analysisSummary/filterByGrid/utils";
import { IOptionItem } from "@/components/ui/dropdown/types";
import { DROPDOWN_ALL_VALUE } from "@/components/ui/dropdownMultiSelect";
import { DATA_TYPE_VALUES, LIMITED_DATA_SUMMARY } from "@/constants/dataset";
import { useAnalysisSummaryDataContext } from "@/contexts/AnalysisSummaryDataContext";
import { capitalizeFirst } from "@/helpers/string";
import { useDebounce } from "@/hooks/useDebounce";
import { DatasetData, DatasetDataType } from "@/types/dataset";
import { isOrdinalType } from "@/utils/dataset";

import useToast, { EToastType } from "../ui/useToast";
import useAnalysisSummaryVisualization from "./useAnalysisSummaryVisualization";

export function useAnalysisDatasetSummary(
  datasetData: DatasetData,
  latestAnalysisId: string | undefined,
  gridsSelected: string[]
) {
  const { showToast } = useToast();
  const [analysisConfiguration, setAnalysisConfiguration] =
    useState<AnalysisConfiguration | null>(null);
  const [filterByOptions, setFilterByOptions] = useState<IOptionItem[]>([]);
  const [viewType, setViewType] = useState<DatasetSectionViewType>(
    DatasetSectionViewType.TABLE
  );
  const { gridValue } = useAnalysisSummaryDataContext();

  const { data: userProfile } = useGetUserProfileAPI();

  const debouncedGridsSelected = useDebounce(gridsSelected, 500);

  const filteredGrids = getFilteredGrids(
    analysisConfiguration?.category?.value,
    debouncedGridsSelected,
    gridValue
  );

  // Fetch data
  const {
    isLoading: isLoadingDatasetSummary,
    data: summaryData,
    error: analysisSummaryError
  } = useGetAnalysisSummaryByDataset(
    datasetData.dataset_id,
    latestAnalysisId,
    analysisConfiguration,
    filteredGrids
  );

  const { data: datasetSchema, error: datasetSchemaError } =
    useGetSchemaDatasetByAnalysis(latestAnalysisId, datasetData.dataset_id);

  // Numeric column check
  const hasNumericColumn = useMemo(
    () =>
      datasetData.columns.some(
        (col) =>
          !isOrdinalType(DATA_TYPE_VALUES[DatasetDataType[col.data_type]])
      ),
    [datasetData.columns]
  );

  const highestScoreGridId = useMemo(() => {
    const isAllSelected = gridsSelected?.[0] === DROPDOWN_ALL_VALUE;
    const filteredGridValue = isAllSelected
      ? gridValue
      : gridValue.filter((grid) => gridsSelected?.includes(grid.value));

    if (filteredGridValue.length === 0) return "";

    const highestScoreGrid = filteredGridValue.reduce(
      (prev, current) => (prev.score > current.score ? prev : current),
      filteredGridValue[0]
    );

    return highestScoreGrid.value;
  }, [gridValue, gridsSelected]);

  const { columns, rows, donutChartData, barChartData } =
    useAnalysisSummaryVisualization(
      summaryData,
      highestScoreGridId,
      userProfile?.timezone
    );

  const shownBarChartData = barChartData.slice(
    0,
    LIMITED_DATA_SUMMARY.BAR_CHART
  );

  const shownDonutChartData = donutChartData.slice(
    0,
    LIMITED_DATA_SUMMARY.DONUT_CHART
  );

  if (donutChartData.length > LIMITED_DATA_SUMMARY.DONUT_CHART) {
    const remainingItems = donutChartData.slice(
      LIMITED_DATA_SUMMARY.DONUT_CHART
    );

    const othersValue = remainingItems.reduce((totalValue, currentItem) => {
      const value = currentItem?.value;
      return typeof value === "number" && !Number.isNaN(value)
        ? totalValue + value
        : totalValue;
    }, 0);

    const othersEntry = { name: "Others", value: othersValue };
    shownDonutChartData.splice(
      LIMITED_DATA_SUMMARY.DONUT_CHART - 1,
      1,
      othersEntry
    );
  }

  // Get sort by options based on the current method and selected grids
  const sortByOptions = useMemo(
    () =>
      getDatasetSummarySortByOptions(
        analysisConfiguration,
        debouncedGridsSelected,
        gridValue
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      analysisConfiguration?.method,
      analysisConfiguration?.category,
      debouncedGridsSelected,
      gridValue
    ]
  );

  const attributesOptions: IOptionItem[] = useMemo(() => {
    return (
      datasetSchema?.schema
        ?.filter(
          (schema) => schema.attribute_name !== "Grid ID" && schema.is_visible
        )
        ?.map((item) => ({
          label: item.attribute_name,
          value: item.id
        })) || []
    );
  }, [datasetSchema]);

  useEffect(() => {
    if (!attributesOptions?.length || !datasetSchema || !datasetData) return;

    setViewType(
      getDefaultViewType(datasetData, attributesOptions, datasetSchema)
    );
  }, [attributesOptions, datasetSchema, datasetData]);

  useEffect(() => {
    if (!datasetSchema) return;
    // Filter all numeric attribute and create options
    const newOptions = datasetSchema.schema
      .filter((item: any) => !isOrdinalType(item.data_type))
      .map((item: any) => ({
        label: item.attribute_name,
        value: item.attribute_name
      }));

    setFilterByOptions((prev) => {
      if (JSON.stringify(prev) === JSON.stringify(newOptions)) return prev;
      return newOptions;
    });
  }, [datasetSchema]);

  useEffect(() => {
    if (!datasetSchema) return;
    const newConfig = getDefaultAnalysisConfig(
      datasetData,
      hasNumericColumn,
      attributesOptions
    );

    setAnalysisConfiguration((prev) => {
      if (JSON.stringify(prev) === JSON.stringify(newConfig)) return prev;
      return newConfig;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datasetData, hasNumericColumn, datasetSchema]);

  useEffect(() => {
    if (
      !analysisSummaryError?.message?.message &&
      !datasetSchemaError?.message.message
    )
      return;
    showToast({
      type: EToastType.ERROR,
      message: capitalizeFirst(
        analysisSummaryError?.message?.message ||
          datasetSchemaError?.message?.message
      )
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisSummaryError, datasetSchemaError]);

  return {
    isLoadingDatasetSummary,
    summaryData,
    columns,
    rows,
    verticalBarChartData: barChartData,
    donutChartData: shownDonutChartData,
    barChartData: shownBarChartData,
    filterByOptions,
    analysisConfiguration,
    viewType,
    attributesOptions,
    debouncedGridsSelected,
    sortByOptions,
    setAnalysisConfiguration,
    setViewType,
    setFilterByOptions,
    hasNumericColumn,
    datasetSchema
  };
}
